import { useEffect, useState } from 'react';
import Intercom, { boot, hide, onHide, onShow, show, shutdown, update } from '@intercom/messenger-js-sdk';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { isSessionValid } from '../auth/storage';
import { isAppLanguage } from '../i18n/config';
import './Intercom.css';

const APP_ID = process.env.REACT_APP_INTERCOM_APP_ID || 'yn9p2m6n';
const LAUNCHER_ID = 'apc-intercom-launcher';
const HIDDEN_PATHS = ['/login', '/register', '/reset-password'];

const placement = {
    alignment: 'right',
    horizontal_padding: 15,
    vertical_padding: 15,
    hide_default_launcher: true,
} as const;

function isHiddenPath(pathname: string) {
    return HIDDEN_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

let bootedMode: string | null = null;
let bootedLanguage = '';

function intercomLanguage(language: string) {
    const code = (language || 'en').toLowerCase();
    if (code.startsWith('zh')) return 'zh-CN';
    const base = code.split('-')[0];
    return isAppLanguage(base) ? base : 'en';
}

function IntercomMessenger() {
    const { pathname } = useLocation();
    const { user, authInitializing } = useAuth();
    const hidden = isHiddenPath(pathname);
    const signedIn = Boolean(user) && isSessionValid();
    const userId = user?.id || user?.email || '';
    const name = user?.displayName || user?.name || user?.email || '';
    const email = user?.email || '';
    const { i18n } = useTranslation();
    const language = intercomLanguage(i18n.resolvedLanguage || i18n.language);
    const [open, setOpen] = useState(false);

    const messengerSettings = () => ({
        app_id: APP_ID,
        ...placement,
        language_override: language,
        ...(signedIn && userId ? { user_id: userId, name, email } : {}),
    });

    useEffect(() => {
        if (!APP_ID || authInitializing) return;

        if (hidden) {
            if (typeof window.Intercom === 'function') shutdown();
            bootedMode = 'off';
            bootedLanguage = '';
            document.body.classList.add('intercom-off');
            return;
        }

        document.body.classList.remove('intercom-off');
        const mode = signedIn && userId ? userId : 'guest';
        if (bootedMode === mode) {
            if (bootedLanguage !== language && typeof window.Intercom === 'function') {
                update({ language_override: language });
                if (window.intercomSettings) window.intercomSettings.language_override = language;
                bootedLanguage = language;
            }
            return;
        }

        const settings = messengerSettings();

        if (typeof window.Intercom !== 'function') {
            Intercom(settings);
        } else if (bootedMode && bootedMode !== 'off') {
            shutdown();
        }
        boot(settings);
        onShow(() => setOpen(true));
        onHide(() => setOpen(false));
        bootedMode = mode;
        bootedLanguage = language;
    }, [hidden, authInitializing, signedIn, userId, name, email, language]);

    const openMessenger = () => {
        const settings = messengerSettings();
        const messenger = window.Intercom as (typeof window.Intercom & { booted?: boolean }) | undefined;
        if (typeof messenger !== 'function') {
            Intercom(settings);
            boot(settings);
        } else if (!messenger.booted) {
            boot(settings);
        }
        show();
        setOpen(true);
    };

    const closeMessenger = () => {
        hide();
        setOpen(false);
    };

    if (hidden || authInitializing) return null;

    return (
        <button
            type="button"
            id={LAUNCHER_ID}
            className="apc-intercom-launcher"
            aria-label={open ? 'Close messages' : 'Messages'}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (open) closeMessenger();
                else openMessenger();
            }}
        >
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7A2.5 2.5 0 0 1 17.5 16H9l-4.2 3.15A.8.8 0 0 1 3.5 18.5V6.5Z" />
            </svg>
        </button>
    );
}

export default IntercomMessenger;
